import { useEffect, useRef } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

/** Background poller — every 25s hits `/coach/messages/unread-total`.
 *  When the count goes UP since last check we (a) fire a Sonner toast
 *  and (b) trigger a browser Notification if the tab is hidden and the
 *  user has granted permission.
 *
 *  Rendered exactly once inside AppLayout so it lives across pages.
 */
export default function CoachMessageNotifier() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lastRef = useRef(null);          // last observed unread count
  const permAskedRef = useRef(false);    // ask permission at most once per session

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await api.get("/coach/messages/unread-total");
        if (cancelled) return;
        const n = Number(data?.unread || 0);
        const prev = lastRef.current;
        lastRef.current = n;
        if (prev !== null && n > prev) {
          const delta = n - prev;
          // In-app toast — always
          toast(
            `${delta} new message${delta === 1 ? "" : "s"}`,
            {
              description: "Tap to open the coach thread.",
              action: {
                label: "Open",
                onClick: () => navigate(user.is_coach ? "/clients" : "/profile"),
              },
              icon: <MessageCircle className="w-4 h-4" />,
            }
          );
          // Browser Notification when tab isn't focused
          try {
            if (typeof Notification !== "undefined" &&
                Notification.permission === "granted" &&
                document.visibilityState !== "visible") {
              const notif = new Notification("Amino Exchange", {
                body: `${delta} new message${delta === 1 ? "" : "s"} on your coach thread`,
                tag: "amino-coach-chat",
              });
              notif.onclick = () => {
                window.focus();
                navigate(user.is_coach ? "/clients" : "/profile");
              };
            }
          } catch { /* silent */ }
        }
      } catch {
        /* silent — treat as 0 */
      }
    };

    // Ask for notification permission once, gently, right after mount
    if (!permAskedRef.current && typeof Notification !== "undefined" &&
        Notification.permission === "default") {
      permAskedRef.current = true;
      // Delay slightly so it doesn't intercept a page load
      setTimeout(() => {
        try { Notification.requestPermission?.(); } catch { /* ignore */ }
      }, 4000);
    }

    tick();
    const iv = setInterval(tick, 25_000);
    // Refresh whenever tab regains focus so we catch missed messages
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      clearInterval(iv);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [user?.id, user?.is_coach, navigate]);

  return null;
}
